// SPDX-License-Identifier: AGPL-3.0-or-later

import {Avatar} from '@app/features/ui/components/Avatar';
import {AvatarDecorationOverlay} from '@app/features/user/components/AvatarDecorationOverlay';
import styles from '@app/features/user/components/modals/tabs/my_profile_tab/CosmeticPickerPreview.module.css';
import {NameplateMedia} from '@app/features/user/components/NameplateMedia';
import NameplateCatalog from '@app/features/user/state/NameplateCatalog';
import Users from '@app/features/user/state/Users';
import {remFromPx} from '@app/features/theme/layout/RemFromPx';
import {getAvatarDecorationSizePx} from '@app/features/user/utils/AvatarDecorationCutout';
import * as NicknameUtils from '@app/features/user/utils/NicknameUtils';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';

const PREVIEW_DESCRIPTOR = msg({message: 'Preview'});
const PREVIEW_AVATAR_PX = 80;
const ROW_AVATAR_PX = 28;

interface CosmeticPickerPreviewProps {
	/** Decoration to show; '' shows none (null would fall back to the equipped one). */
	decorationId: string;
	/** Nameplate to show behind the member-list row; null shows none. */
	nameplateId: string | null;
	itemName: string;
	itemDescription?: string | null;
}

/** Discord-style live preview: the user's own avatar, name and member-list row with the chosen cosmetic. */
export const CosmeticPickerPreview = observer(function CosmeticPickerPreview({
	decorationId,
	nameplateId,
	itemName,
	itemDescription,
}: CosmeticPickerPreviewProps) {
	const {i18n} = useLingui();
	const user = Users.currentUser;
	if (!user) return null;
	const nameplate = nameplateId ? NameplateCatalog.getById(nameplateId) : undefined;
	const displayName = NicknameUtils.getDisplayName(user);
	const frameStyle = {
		'--avatar-decoration-size': remFromPx(getAvatarDecorationSizePx(PREVIEW_AVATAR_PX)),
	} as React.CSSProperties;
	return (
		<div className={styles.previewPane} data-flx="user.cosmetic-picker.preview">
			<div className={styles.previewLabel}>{i18n._(PREVIEW_DESCRIPTOR)}</div>
			<div className={styles.card}>
				<div className={styles.banner} />
				<div className={styles.identity}>
					<div className={styles.avatarFrame} style={frameStyle}>
						<Avatar user={user} size={PREVIEW_AVATAR_PX} forceAnimate />
						<AvatarDecorationOverlay userId={user.id} decorationId={decorationId} avatarSize={PREVIEW_AVATAR_PX} />
					</div>
					<div className={styles.displayName}>{displayName}</div>
					<div className={styles.username}>{NicknameUtils.formatTagForStreamerMode(user.tag)}</div>
				</div>
				<div className={styles.memberRow}>
					{nameplate ? (
						<div className={styles.memberRowNameplate} aria-hidden>
							<NameplateMedia
								src={nameplate.image}
								catalogId={nameplate.id}
								active
								className={styles.memberRowMedia}
								title={nameplate.name}
							/>
							<div className={styles.memberRowFade} />
						</div>
					) : null}
					<div className={styles.memberRowAvatar}>
						<Avatar user={user} size={ROW_AVATAR_PX} />
					</div>
					<div className={styles.memberRowName}>{displayName}</div>
				</div>
			</div>
			<div className={styles.itemInfo}>
				<div className={styles.itemName}>{itemName}</div>
				{itemDescription ? <div className={styles.itemDescription}>{itemDescription}</div> : null}
			</div>
		</div>
	);
});
